import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  BehaviorSubject,
  Observable,
} from 'rxjs';
import { map } from 'rxjs/operators';

import {
  Client,
  Department,
  Shift,
} from '@app/_models';
import {
  InAppNotificationService,
} from '@app/_services/inAppNotification.service';
import { environment } from '@environments/environment';

interface DataObject {
  data: {};
}

@Injectable({providedIn: 'root'})
export class ClientRepositoryService {
  /**
   * Used to access the clients via an observable
   */
  public clients = new BehaviorSubject<Client[]>(new Array<Client>());

  constructor(private httpClient: HttpClient, private notificationService: InAppNotificationService) {
    this.refreshClients();
  }

  /**
   * Refresh clients in repository
   */
  public refreshClients(): void {
    this.fetchClients().subscribe(res => {
      this.clients.next(res);
    }, (err) => {
      this.notificationService.fetchError('clients', err.status);
    });
  }

  /**
   * Fetch a single client from the repository
   */
  public getClient(clientId: number): Client | undefined {
    return this.clients.getValue()?.find(c => c.id === clientId);
  }

  /**
   * Fetch a single department from the repository
   */
  public getDepartment(clientId: number, departmentId: number): Department | undefined {
    return this.getClient(clientId)?.departments.find(d => d.id === departmentId);
  }

  /**
   * Fetch a single shift from the repository
   */
  public getShift(clientId: number, departmentId: number, shiftId: number): Shift | undefined {
    return this.getDepartment(clientId, departmentId)?.shifts.find(s => s.id === shiftId);
  }

  /**
   * Fetch all departments from the repository
   */
  public getDepartments(clientId: number): Department[] | undefined {
    return this.getClient(clientId)?.departments;
  }

  /**
   * Fetch all shifts from the repository
   */
  public getShifts(clientId: number, departmentId: number): Shift[] | undefined {
    return this.getDepartment(clientId, departmentId)?.shifts;
  }

  /**
   * Fetch all clients from the repository
   */
  private fetchClients(): Observable<Client[]> {
    return this.httpClient.get<DataObject>(`${environment.API_URL}/clients/get-clients`).pipe(map(result => {
      return result.data as Client[];
    }));
  }
}
